import React, { useState, useEffect } from "react";
import AdminTopNavBar from "../common/AdminTopNavBar";
import "../../styles/Admin.css";
import AdminFooter from "../common/AdminFooter";
import AdminSideBar from "./AdminSideBar";
import getUserMenuItems from "./AdminMenuContent";
import { api, adminReqConfig } from "../../include/api";
import { useNotification } from "../../Toast/ToastProvider";
import "../../styles/CategoryProductList.css";
import { ERROR, SUCCESS } from "../../include/constant";
import { useNavigate } from "react-router-dom";
import {
  formatShortMonthDateYearWithTime,
  validatePincode,
} from "../../include/function";

const AddPincode = () => {
  const toast = useNotification();
  const navigate = useNavigate();
  const homeLink = "/admin";
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const toggleSidebar = () => setIsSidebarMinimized(!isSidebarMinimized);
  const menuContent = getUserMenuItems();

  const urlParams = new URLSearchParams(window.location.search);
  const pincodeIdFromUrl = urlParams.get("id");
  const isViewMode = urlParams.get("view") === "true";
  const isEditMode = !!pincodeIdFromUrl && !isViewMode;

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!pincodeIdFromUrl);
  const [pincode, setPincode] = useState("");
  const [createTimestamp, setCreateTimestamp] = useState("");
  const [pincodeError, setPincodeError] = useState("");

  const getButtonStyle = (isPrimary = false) => ({
    border: "none",
    padding: "8px 20px",
    fontWeight: "500",
    borderRadius: "5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: saving ? "not-allowed" : "pointer",
    opacity: saving ? 0.7 : 1,
    ...(isPrimary
      ? {
          backgroundColor: saving ? "#1565c0" : "#1976d2",
          color: "#fff",
        }
      : {
          backgroundColor: saving ? "#e0e0e0" : "#bed9f8ff",
          color: saving ? "#9e9e9e" : "#1976d2",
        }),
  });

  const renderLoadingSpinner = (size = "sm") => (
    <i
      className={`fa fa-spinner fa-spin ${size === "sm" ? "me-2" : "me-3"}`}
      aria-hidden="true"
      style={{ fontSize: size === "sm" ? "0.9rem" : "1.1rem" }}
    ></i>
  );

  const fetchPincodeDetails = (id) => {
    setLoading(true);
    api
      .get(`/pincode/?id=${id}`, adminReqConfig())
      .then((res) => {
        if (res.status === 200) {
          const item = Array.isArray(res.data.data)
            ? res.data.data[0]
            : res.data.data;
          if (!item) return;
          setPincode(item.pincode || "");
          setCreateTimestamp(item.createTimestamp || "");
        }
      })
      .catch(() => {
        toast({ type: ERROR, message: "Failed to load pincode details." });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (pincodeIdFromUrl) {
      fetchPincodeDetails(pincodeIdFromUrl);
    }
  }, [pincodeIdFromUrl]);

  const validate = () => {
    if (!pincode.trim()) {
      setPincodeError("Pincode is required");
      toast({ type: ERROR, message: "Pincode is required" });
      return false;
    }
    if (!validatePincode(pincode.trim())) {
      setPincodeError("Enter a valid 6-digit pincode");
      toast({ type: ERROR, message: "Enter a valid 6-digit pincode" });
      return false;
    }
    setPincodeError("");
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { pincode: pincode.trim() };
      const res = isEditMode
        ? await api.put(
            `/pincode/?id=${pincodeIdFromUrl}`,
            payload,
            adminReqConfig(),
          )
        : await api.post("/pincode/", payload, adminReqConfig());

      if (![200, 201].includes(res.status)) throw new Error("Save failed");

      toast({
        type: SUCCESS,
        message: isEditMode
          ? "Pincode updated successfully!"
          : "Pincode added successfully!",
      });
      navigate("/admin/pincode/pincodeList");
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (isEditMode ? "Failed to update pincode" : "Failed to add pincode");
      toast({ type: ERROR, message: msg });
    } finally {
      setSaving(false);
    }
  };

  const clearFields = () => {
    if (saving || isViewMode) return;
    setPincode("");
    setPincodeError("");
  };

  const pageTitle = isViewMode
    ? "PINCODE DETAILS"
    : isEditMode
      ? "EDIT PINCODE"
      : "ADD NEW PINCODE";

  return (
    <>
      <AdminSideBar
        menuContent={menuContent}
        isMinimized={isSidebarMinimized}
      />
      <AdminTopNavBar
        toggleSidebar={toggleSidebar}
        isSidebarMinimized={isSidebarMinimized}
        homeLink={homeLink}
        title={pageTitle}
      />
      <div
        className="content-wrapper p-0"
        style={{
          marginLeft: isSidebarMinimized ? "80px" : "250px",
          flex: "1",
        }}
      >
        <section
          style={{
            backgroundColor: "#F1F4F9",
            padding: "30px",
            marginTop: "70px",
            marginBottom: "40px",
            minHeight: "80vh",
          }}
        >
          <div className="container-fluid">
            <div className="row">
              <div className="col-lg-12">
                <div className="card border-0">
                  <div className="d-flex justify-content-between align-items-center p-16">
                    <h6 className="mb-0 fs-5">Pincode Information</h6>
                    {!isViewMode && (
                      <button
                        onClick={clearFields}
                        disabled={saving}
                        style={getButtonStyle(false)}
                      >
                        {saving ? (
                          <>
                            {renderLoadingSpinner("sm")}
                            Clearing...
                          </>
                        ) : (
                          "Clear"
                        )}
                      </button>
                    )}
                  </div>
                  <div className="card-body">
                    {loading ? (
                      <p>Loading...</p>
                    ) : (
                      <>
                        <div className="row">
                          <div className="col-md-6 mb-16">
                            <h6 className="text-md mb-16 fw-bold">Pincode</h6>
                            <input
                              type="text"
                              className="form-control coupon-add-input"
                              placeholder="Enter 6-digit pincode"
                              value={pincode}
                              maxLength={6}
                              onChange={(e) => {
                                if (saving || isViewMode) return;
                                const value = e.target.value.replace(/\D/g, "");
                                setPincode(value);
                                setPincodeError("");
                              }}
                              disabled={saving || isViewMode}
                            />
                            {pincodeError && (
                              <div className="text-danger mt-1">
                                {pincodeError}
                              </div>
                            )}
                          </div>
                          {(isEditMode || isViewMode) && createTimestamp && (
                            <div className="col-md-6 mb-16">
                              <h6 className="text-md mb-16 fw-bold">
                                Created Timestamp
                              </h6>
                              <input
                                type="text"
                                className="form-control coupon-add-input"
                                value={formatShortMonthDateYearWithTime(
                                  createTimestamp,
                                )}
                                disabled
                              />
                            </div>
                          )}
                        </div>
                        <div className="d-flex justify-content-end gap-8 mt-24">
                          {isViewMode ? (
                            <button
                              onClick={() =>
                                navigate("/admin/pincode/pincodeList")
                              }
                              style={getButtonStyle(false)}
                            >
                              Back
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={handleSave}
                                disabled={saving}
                                style={getButtonStyle(true)}
                              >
                                {saving ? (
                                  <>
                                    {renderLoadingSpinner("sm")}
                                    {isEditMode ? "Updating..." : "Adding..."}
                                  </>
                                ) : isEditMode ? (
                                  "Update"
                                ) : (
                                  "Add"
                                )}
                              </button>
                              <button
                                onClick={clearFields}
                                disabled={saving}
                                style={getButtonStyle(false)}
                              >
                                {saving ? (
                                  <>
                                    {renderLoadingSpinner("sm")}
                                    Clearing...
                                  </>
                                ) : (
                                  "Clear All"
                                )}
                              </button>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <AdminFooter isSidebarMinimized={isSidebarMinimized} />
    </>
  );
};

export default AddPincode;
